import React from "react";
import { motion } from "framer-motion";
import { BookOpen, PanelLeftOpen, SlidersHorizontal } from "lucide-react";
import { ForgeDarkIcon, ForgeLightIcon, SpectraDarkSvg, SpectraSvg, SparkDarkSvg, SparkSvg } from "../../assets";
import PhotonLogo from "../Common/PhotonLogo";
import { useTheme } from "../../context/ThemeProvider";
import { useNavigate, useLocation } from "react-router-dom";
import ROUTES from "../../router/routes";
import UIStore from "../../store/uiStore";
import ScheduledJobsIcon from "./ScheduledJobsIcon";

const SidebarCollapsedContent = ({ onExpand, handleSparkClick }) => {
  const { theme, colorScheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const setShowSidebar = UIStore((state) => state.setShowSidebar);

  const isForgeActive = location.pathname.startsWith(ROUTES.FORGE.ROOT);
  const isSpectraActive = location.pathname.startsWith('/spectra');
  const isSparkActive = location.pathname.startsWith('/chat') || location.pathname === '/';
  const isScheduledJobsActive = location.pathname === ROUTES.SETTINGS.SCHEDULED_JOBS;
  const isSettingsActive = location.pathname.startsWith(ROUTES.SETTINGS.ROOT) && !isScheduledJobsActive;
  
  const handleNavigate = (route) => {
    navigate(route);
    // Close sidebar on mobile/tablet after navigation
    if (window.innerWidth < 940) {
      setShowSidebar(false);
    }
  };
  return (
    <>
      <div className="flex flex-col items-center mt-3 gap-4">
        <PhotonLogo className="h-8 w-8" />
        <motion.button
          className={`photon-rail-control w-11 h-11 flex items-center justify-center rounded-lg ${
            theme === "dark"
              ? "hover:bg-fill-canvas-hover focus:bg-fill-canvas-hover"
              : "hover:bg-fill-gray-200 focus:bg-fill-gray-200"
          } transition-colors mb-4`}
          onClick={onExpand}
          aria-label="Expand sidebar"
          title="Expand sidebar"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <PanelLeftOpen size={20} className="text-ink-gray-500 dark:text-ink-gray-400" aria-hidden="true" />
        </motion.button>
        <motion.button
          className={`photon-rail-control w-11 h-11 flex items-center justify-center rounded-xl ${
            isForgeActive
              ? theme === "dark"
                ? "bg-fill-canvas-hover"
                : "bg-fill-gray-200"
              : theme === "dark"
              ? "hover:bg-fill-canvas-hover focus:bg-fill-canvas-hover"
              : "hover:bg-fill-gray-200 focus:bg-fill-gray-200"
          } transition-colors mb-2`}
          aria-label="Forge"
          aria-current={isForgeActive ? "page" : undefined}
          title="Forge"
          onClick={() => handleNavigate(ROUTES.FORGE.ROOT)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {colorScheme === "docs" ? <ForgeLightIcon className="photon-navigation-icon photon-product-icon text-ink-gray-500" aria-hidden="true" /> : theme === "dark" ? (
            <ForgeDarkIcon className="w-6 h-6" />
          ) : (
            <ForgeLightIcon className="w-6 h-6" />
          )}
        </motion.button>
        <motion.button
          className={`photon-rail-control w-11 h-11 flex items-center justify-center rounded-xl ${
            isScheduledJobsActive
              ? theme === "dark"
                ? "bg-fill-canvas-hover"
                : "bg-fill-gray-200"
              : theme === "dark"
              ? "hover:bg-fill-canvas-hover focus:bg-fill-canvas-hover"
              : "hover:bg-fill-gray-200 focus:bg-fill-gray-200"
          } transition-colors mb-2`}
          aria-label="Scheduled Jobs"
          aria-current={isScheduledJobsActive ? "page" : undefined}
          title="Scheduled Jobs"
          onClick={() => handleNavigate(ROUTES.SETTINGS.SCHEDULED_JOBS)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <ScheduledJobsIcon className="h-5 w-5" />
        </motion.button>
        <motion.button
          className={`photon-rail-control w-11 h-11 flex items-center justify-center rounded-xl ${
            isSpectraActive
              ? theme === "dark"
                ? "bg-fill-canvas-hover"
                : "bg-fill-gray-200"
              : theme === "dark"
              ? "hover:bg-fill-canvas-hover focus:bg-fill-canvas-hover"
              : "hover:bg-fill-gray-200 focus:bg-fill-gray-200"
          } transition-colors mb-2`}
          aria-label="Spectra"
          aria-current={isSpectraActive ? "page" : undefined}
          title="Spectra"
          onClick={() => handleNavigate(ROUTES.SPECTRA.ROOT)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {colorScheme === "docs" ? <SpectraSvg className="photon-navigation-icon photon-product-icon text-ink-gray-500" aria-hidden="true" /> : theme === "dark" ? (
            <SpectraDarkSvg className="w-6 h-6" />
          ) : (
            <SpectraSvg className="w-6 h-6" />
          )}
        </motion.button>
        <motion.button
          className={`photon-rail-control w-11 h-11 flex items-center justify-center rounded-xl ${
            isSparkActive
              ? theme === "dark"
                ? "bg-fill-canvas-hover"
                : "bg-fill-gray-200"
              : theme === "dark"
              ? "hover:bg-fill-canvas-hover focus:bg-fill-canvas-hover"
              : "hover:bg-fill-gray-200 focus:bg-fill-gray-200"
          } transition-colors mb-2`}
          aria-label="Spark"
          aria-current={isSparkActive ? "page" : undefined}
          title="Spark"
          onClick={handleSparkClick}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {colorScheme === "docs" ? <SparkSvg className="photon-navigation-icon photon-product-icon text-ink-gray-500" aria-hidden="true" /> : theme === "dark" ? (
            <SparkDarkSvg className="w-6 h-6" />
          ) : (
            <SparkSvg className="w-6 h-6" />
          )}
        </motion.button>
      </div>
      <div className="flex flex-col items-center gap-2 mb-4">
        <motion.a
          href="/docs"
          target="_blank"
          rel="noopener noreferrer"
          className={`photon-rail-control w-11 h-11 flex flex-col items-center justify-center gap-0.5 rounded-xl ${
            theme === "dark"
              ? "hover:bg-fill-canvas-hover focus:bg-fill-canvas-hover"
              : "hover:bg-fill-gray-200 focus:bg-fill-gray-200"
          } transition-colors`}
          aria-label="Documentation"
          title="Documentation"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <BookOpen className="w-5 h-5 text-ink-gray-500 dark:text-ink-gray-400" />
          <span className="text-[10px] leading-3">Docs</span>
        </motion.a>
        <motion.button
          className={`photon-rail-control w-11 h-11 flex flex-col items-center justify-center gap-0.5 rounded-xl ${
            isSettingsActive
              ? theme === "dark"
                ? "bg-fill-canvas-hover"
                : "bg-fill-gray-200"
              : theme === "dark"
              ? "hover:bg-fill-canvas-hover focus:bg-fill-canvas-hover"
              : "hover:bg-fill-gray-200 focus:bg-fill-gray-200"
          } transition-colors`}
          aria-label="Settings"
          aria-current={isSettingsActive ? "page" : undefined}
          title="Settings"
          onClick={() => handleNavigate(ROUTES.SETTINGS.ROOT)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <SlidersHorizontal className="w-5 h-5" aria-hidden="true" />
          <span className="text-[10px] leading-3">Settings</span>
        </motion.button>
      </div>
    </>
  );
};

export default SidebarCollapsedContent;
